import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import SearchPreviewNew from './SearchPreviewNew';

const ANIMATION_DURATION = 200; // Consistent animation timing

function HeaderSearchBar() {
    const navigate = useNavigate();
    const searchInputRef = useRef(null);
    const searchContainerRef = useRef(null);
    const isEscapingRef = useRef(false);
    const isClickingPreviewItemRef = useRef(false); // Flag to track preview item clicks
    const [searchQuery, setSearchQuery] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    
    const resetSearchState = useCallback(() => {
        setSearchQuery('');
        // Explicitly blur the input when resetting state
        if (searchInputRef.current) {
            searchInputRef.current.blur(); 
        }
        setShowPreview(false); // Explicitly hide preview
        setIsExpanded(false); // Explicitly collapse bar
    }, []);
    
    const handleClearSearch = useCallback(() => {
        setSearchQuery('');
        resetSearchState();
    }, [navigate, searchQuery, resetSearchState]);

    const handleSearch = useCallback((inputQuery) => {
        const queryToSearch = inputQuery || searchQuery;
        if (!queryToSearch) return;
        
        navigate(`/search?q=${encodeURIComponent(queryToSearch)}`);
        resetSearchState();
    }, [navigate, searchQuery, resetSearchState]);

    const handleCompanyClick = useCallback((company) => {
        isClickingPreviewItemRef.current = true; // Set flag before potentially triggering blur
        navigate(`/company/${company.id}`);
        resetSearchState(); // Reset state AFTER navigation starts
    }, [navigate, resetSearchState]);

    const handleInputFocus = useCallback(() => {
        console.log('[HeaderSearchBar] Input focused, current query:', searchQuery);
        setIsExpanded(true);
        // Show the preview immediately when focusing
        setShowPreview(true);
        console.log('[HeaderSearchBar] Set showPreview to true');
    }, [searchQuery]);

    const handleInputBlur = useCallback((e) => {
        console.log('[HeaderSearchBar] Input blur event');
        // If the blur was triggered by clicking a preview item, do nothing here
        if (isClickingPreviewItemRef.current) {
            isClickingPreviewItemRef.current = false; // Reset flag
            console.log('[HeaderSearchBar] Blur caused by preview click, ignoring.');
            return;
        }

        // Check if focus moved to the search preview
        const isMovingToPreview = document.querySelector('.search-preview-container')?.contains(e.relatedTarget);
        console.log('[HeaderSearchBar] Moving to preview?', isMovingToPreview);
        
        if (!isMovingToPreview) {
            setIsExpanded(false);
            setShowPreview(false);
            console.log('[HeaderSearchBar] Reset states on blur');
        }
    }, []);
    
    // Escape key handler
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            handleSearch();
        } else if (e.key === 'Escape') {
            if (searchInputRef.current) {
                searchInputRef.current.blur();
            }
            e.preventDefault();
            e.stopPropagation();
        }
    }, [handleSearch]);
    
    // Global click handler to reset when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                searchContainerRef.current && 
                !searchContainerRef.current.contains(e.target) &&
                !document.querySelector('.search-preview-container')?.contains(e.target)
            ) {
                setShowPreview(false);
                setIsExpanded(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div 
            ref={searchContainerRef}
            className={`header-search-container ${isExpanded ? 'focused' : ''}`}
        >
            <div className={`search-icon-wrapper ${isExpanded ? 'focused' : ''}`}>
                <SearchIcon />
            </div>
            <input
                ref={searchInputRef}
                type="text"
                className="header-search-input"
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
            />
            <SearchPreviewNew
                query={searchQuery}
                parentRef={searchInputRef}
                anchorRef={searchContainerRef}
                onlyShowCompanySearch={false}
                handleClick={handleCompanyClick}
                handleSearch={handleSearch}
                onClear={handleClearSearch}
                isVisible={showPreview}
                isClickingPreviewItemRef={isClickingPreviewItemRef}
            />
        </div>
    );
}

export default HeaderSearchBar; 